import { useCallback, useEffect, useRef, useState } from "react";

const DEBOUNCE_MS = 600;

export function useQuantityInput(
  initialQuantity: number,
  onUpdate: (quantity: number) => void,
  isPending: boolean,
  maxStock: number = 999,
) {
  const [localQuantity, setLocalQuantity] = useState(initialQuantity);
  const [inputValue, setInputValue] = useState(String(initialQuantity));
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingValueRef = useRef<number | null>(null);

  // Sync with server quantity when not pending
  useEffect(() => {
    if (!isPending) {
      setLocalQuantity(initialQuantity);
      setInputValue(String(initialQuantity));
    }
  }, [isPending, initialQuantity]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const commitQuantityChange = useCallback(
    (quantity: number) => {
      setLocalQuantity(quantity);
      setInputValue(String(quantity));
      onUpdate(quantity);
    },
    [onUpdate],
  );

  // Flush pending debounced value immediately
  const flushDebounce = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (pendingValueRef.current !== null) {
      commitQuantityChange(pendingValueRef.current);
      pendingValueRef.current = null;
    }
  }, [commitQuantityChange]);

  const handleDecrement = useCallback(() => {
    flushDebounce();
    const newQuantity = localQuantity - 1;
    if (newQuantity >= 1) {
      commitQuantityChange(newQuantity);
    }
  }, [localQuantity, commitQuantityChange, flushDebounce]);

  const handleIncrement = useCallback(() => {
    flushDebounce();
    const newQuantity = localQuantity + 1;
    if (newQuantity <= maxStock) {
      commitQuantityChange(newQuantity);
    }
  }, [localQuantity, maxStock, commitQuantityChange, flushDebounce]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      // Prevent input longer than maxStock digits
      const maxLength = String(maxStock).length;
      if (value.length > maxLength) {
        return;
      }

      // Only allow numbers
      if (value !== "" && !/^\d+$/.test(value)) {
        return;
      }

      setInputValue(value);

      const numValue = parseInt(value, 10);

      if (value === "" || isNaN(numValue)) {
        setLocalQuantity(NaN);
        // Clear any pending debounce
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
          pendingValueRef.current = null;
        }
        return;
      }

      // Clamp to max for local display
      const clampedValue = Math.min(Math.max(numValue, 1), maxStock);
      setLocalQuantity(clampedValue);

      // Clear existing debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Set new debounced commit
      pendingValueRef.current = clampedValue;
      debounceRef.current = setTimeout(() => {
        if (pendingValueRef.current !== null) {
          commitQuantityChange(pendingValueRef.current);
          pendingValueRef.current = null;
          debounceRef.current = null;
        }
      }, DEBOUNCE_MS);
    },
    [maxStock, commitQuantityChange],
  );

  const handleInputBlur = useCallback(() => {
    // Flush any pending change immediately on blur
    flushDebounce();

    let newQuantity = parseInt(inputValue, 10);
    if (isNaN(newQuantity) || newQuantity < 1) {
      newQuantity = 1;
    } else if (newQuantity > maxStock) {
      newQuantity = maxStock;
    }

    if (newQuantity !== localQuantity) {
      commitQuantityChange(newQuantity);
    } else if (inputValue !== String(newQuantity)) {
      setInputValue(String(newQuantity));
    }
  }, [
    inputValue,
    localQuantity,
    maxStock,
    commitQuantityChange,
    flushDebounce,
  ]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        // Flush and blur on Enter
        flushDebounce();
        e.currentTarget.blur();
      }
    },
    [flushDebounce],
  );

  return {
    inputValue,
    localQuantity,
    handleDecrement,
    handleIncrement,
    handleInputChange,
    handleInputBlur,
    handleInputKeyDown,
  };
}
